import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { produce } from 'immer'
import { findIndex } from 'lodash-es'
import { TypeOf } from 'yup'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Button, Stack, Typography } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeleteButton, SaveButton } from '@ui/actions'
import { Form } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useEditElementStore, useModelStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { getModel } from '@queries'
import {
  addFoundation,
  deleteFoundation,
  updateFoundation as updateFoundationQuery,
} from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import FormFields from '../../../RoofDrawing/components/RoofForm/components/FormFields'
import FoundationScene from '../FoundationScene'
import { foundationSchema } from './schema'

interface Props {
  selectedElement: string
  setSelectedElement: (element: string | null) => void
}

const FoundationForm = ({ selectedElement, setSelectedElement }: Props): ReactElement => {
  const { projectId } = useParams()

  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const invalidateResults = useResultsInvalidation()

  const foundationElements = useModelStore(state => state.model.foundationElements)
  const foundation = foundationElements.find(element => element.guid === selectedElement)
  const updateFoundation = useModelStore(state => state.updateFoundation)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const defaultValues = useMemo(() => {
    if (!foundation) return foundationSchema.getDefault()

    return {
      ...foundationSchema.getDefault(),
      guid: foundation.guid,
      points: foundation.shape.points.map(p => ({ x: p.x, y: p.y, z: p.z })),
    }
  }, [foundation])

  const { mutateAsync, isLoading } = useMutation(
    (data: TypeOf<typeof foundationSchema>) =>
      updateFoundationQuery.request(projectId as string, selectedElement, data.points),
    {
      onSuccess: async (data: Foundation, variables) => {
        if (!foundation || !variables.points) return

        invalidateResults(projectId as string)

        const queryData = queryClient.getQueryData<PlanarModel>(getModel.getKey(projectId))
        if (queryData) {
          const newModel = produce(queryData, draft => {
            const foundationIndex = findIndex(draft.foundationElements, { guid: data.guid })
            if (foundationIndex !== -1) {
              draft.foundationElements[foundationIndex] = {
                ...draft.foundationElements[foundationIndex],
                ...data,
              }
            }
          })
          queryClient.setQueryData(getModel.getKey(projectId), newModel)
        }

        updateFoundation({
          guid: foundation.guid,
          shape: {
            ...foundation.shape,
            points: variables.points.map(point => new ImmutableVector3(point.x, point.y, point.z)),
          },
        })

        enqueueSnackbar('Fundament erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Fundaments'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: deleteAsync, isLoading: isDeleting } = useMutation(
    (guid: string) => deleteFoundation.request(projectId as string, guid),
    {
      onSuccess: async () => {
        setSelectedElement(null)
        setActiveElement(null)
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        enqueueSnackbar('Fundament erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen des Fundaments'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: duplicateAsync, isLoading: isDuplicating } = useMutation(
    async () => {
      if (!foundation || !projectId) throw new Error('Foundation or projectId is missing')

      const newModel = await addFoundation.request(projectId)

      const existingGuids = new Set(foundationElements.map(f => f.guid))
      const newFoundation = newModel.foundationElements.find(
        (f: Foundation) => !existingGuids.has(f.guid),
      )

      if (!newFoundation) {
        throw new Error('Failed to find newly created foundation element')
      }

      const translatedPoints = foundation.shape.points.map(p => ({
        x: p.x,
        y: p.y,
        z: p.z + 1,
      }))

      await updateFoundationQuery.request(projectId, newFoundation.guid, translatedPoints)

      return newFoundation.guid
    },
    {
      onSuccess: async newFoundationGuid => {
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        setSelectedElement(newFoundationGuid)
        enqueueSnackbar('Fundament erfolgreich dupliziert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Duplizieren des Fundaments'), {
          variant: 'error',
        })
      },
    },
  )

  if (!foundation) {
    return <></>
  }

  return (
    <Box>
      <Typography variant="h5" data-cy="mode-label">
        Fundament
      </Typography>
      <Form
        key={selectedElement}
        onSubmit={data => mutateAsync(data)}
        defaultValues={defaultValues}
        validationSchema={foundationSchema}
        data-cy="foundation-geometry-form"
      >
        {selectedElement && (
          <>
            <FoundationScene guid={selectedElement} />

            <FormFields
              selectedElement={selectedElement}
              showMarker
              zComputed={false}
              setZComputed={() => undefined}
              showZComputedSwitch={false}
              showCalculateZFromOtherPoints={false}
              showPointRotation={false}
              showTranslationAndRotation={false}
              syncZOnChange
            />
          </>
        )}
        <Stack direction="row" justifyContent="end" spacing={1} mt={1}>
          <SaveButton
            type="submit"
            loading={isLoading}
            fullWidth
            disabled={isDeleting}
            data-cy="btn-submit"
          >
            Speichern
          </SaveButton>
          <Button
            variant="contained"
            onClick={() => duplicateAsync()}
            size="small"
            fullWidth
            disabled={isDuplicating || !foundation}
            startIcon={<ContentCopyIcon />}
            data-cy="btn-duplicate-foundation"
          >
            Duplizieren
          </Button>
          <DeleteButton
            onClick={() => deleteAsync(selectedElement)}
            size="small"
            fullWidth
            loading={isDeleting}
            disabled={isLoading}
            data-cy="btn-delete-foundation"
          >
            Löschen
          </DeleteButton>
        </Stack>
      </Form>
    </Box>
  )
}

export default FoundationForm
